import { z, type ZodType } from 'zod';

const baseError = z.object({
  code: z.string(),
  title: z.string().optional(),
  message: z.string(),
  stack: z.string().optional(),
  isRetryable: z.boolean(),
});

const rateLimited = baseError.extend({
  code: z.literal('rate-limited'),
  providerLimitHit: z.boolean(),
  isRetryable: z.literal(true),
});

const freeUserRatedLimited = baseError.extend({
  code: z.literal('free-user-rate-limited'),
  isRetryable: z.literal(true),
});

const projectTooBig = baseError.extend({
  code: z.literal('project-too-big'),
});

const overloaded = baseError.extend({
  code: z.literal('overloaded'),
  isRetryable: z.literal(true),
});

const requestTooLarge = baseError.extend({
  code: z.literal('request-too-large'),
  isRetryable: z.literal(false),
});

const imageTooLarge = baseError.extend({
  code: z.literal('image-too-large'),
  isRetryable: z.literal(false),
});

const apiError = baseError.extend({
  code: z.literal('api-error'),
});

const networkError = baseError.extend({
  code: z.literal('network-error'),
});

const usageBlocked = baseError.extend({
  code: z.literal('usage-blocked'),
  isRetryable: z.literal(false),
});

const billingError = baseError.extend({
  code: z.literal('billing-error'),
  isRetryable: z.literal(true),
});

const contentFilter = baseError.extend({
  code: z.literal('content-filter'),
  isRetryable: z.literal(false),
});

const loginRequiredError = baseError.extend({
  code: z.literal('login-required'),
  isRetryable: z.literal(false),
});

const unknownError = baseError.extend({
  code: z.literal('unknown'),
});

const badServerResponse = baseError.extend({
  code: z.literal('bad-server-response'),
  status: z.number(),
  body: z.string(),
  isRetryable: z.literal(true),
});

const siteCreationError = baseError.extend({
  code: z.literal('site-creation-error'),
  isRetryable: z.literal(true),
  error: z.string().optional(),
});

const siteDeploymentError = baseError.extend({
  code: z.literal('site-deployment-error'),
  isRetryable: z.boolean(),
  error: z.string().optional(),
});

const deploymentUnauthorized = baseError.extend({
  code: z.literal('deployment-unauthorized'),
  isRetryable: z.literal(false),
});

const fixDiffError = baseError.extend({
  code: z.literal('fix-diff-error'),
  fileContent: z.string(),
  diff: z.string(),
});

const fixLazyError = baseError.extend({
  code: z.literal('fix-lazy-error'),
});

const fixBracketError = baseError.extend({
  code: z.literal('fix-bracket-error'),
});

const errorReasoningError = baseError.extend({
  code: z.literal('error-reasoning-error'),
});

export type ParseError<T extends ZodType = typeof baseError> = Error & z.infer<T | typeof unknownError>;

export interface ErrorParser<T extends ZodType> {
  schema: T;
  parse({ schema }: { schema: T }, response: Response, unknownMessage?: string): Promise<ParseError<T>>;
  parse({ schema }: { schema: T }, data: string | object, unknownMessage?: string): ParseError<T>;
}

export abstract class ClientError<T> extends Response {
  static parse<T extends ZodType>(
    { schema }: { schema: T },
    response: Response,
    unknownMessage?: string,
  ): Promise<ParseError<T>>;
  static parse<T extends ZodType>(
    { schema }: { schema: T },
    data: string | object,
    unknownMessage?: string,
  ): ParseError<T>;
  static parse<T extends ZodType>(
    { schema }: { schema: T },
    data: Response | string | object,
    unknownMessage = 'An unknown error occurred.',
  ) {
    const makeError = (error: z.infer<T>) => {
      return Object.assign(new Error(error.message), error, {
        name: kebabToPascal(error.code),
      }) as ParseError<T>;
    };

    const handleData = (data: string | object, response?: Response) => {
      try {
        const json = typeof data === 'string' ? JSON.parse(data) : data;

        try {
          return makeError(schema.parse(json) as z.infer<T>);
        } catch {
          return makeError(
            unknownError.parse({
              stack: json.stack,
              message: json.message ?? unknownMessage,
              isRetryable: true,
              ...(json as {}),
              code: 'unknown',
            } satisfies z.infer<typeof unknownError>),
          );
        }
      } catch {
        if (response) {
          return makeError(
            badServerResponse.parse({
              code: 'bad-server-response',
              message: `An unexpected status code ${response.status} response was received from the server.`,
              status: response.status,
              isRetryable: true,
              body: typeof data === 'string' ? data : '',
            }) as z.infer<typeof badServerResponse>,
          );
        }

        return makeError({
          code: 'unknown',
          message: unknownMessage,
          isRetryable: true,
        } satisfies z.infer<typeof unknownError>);
      }
    };

    if (data instanceof Response) {
      return data
        .text()
        .then((text) => handleData(text, data))
        .catch((error) => handleData(error, data));
    }

    return handleData(data);
  }

  constructor(
    status: number,
    public data: T,
    responseInit?: ResponseInit,
  ) {
    super(JSON.stringify(data), {
      status,
      headers: { 'content-type': 'application/json' },
      ...responseInit,
    });
  }
}

export type ChatErrorCode = z.infer<typeof ChatError.schema>['code'];

export class ChatError extends ClientError<z.infer<typeof ChatError.schema>> {
  static schema = z.union([
    rateLimited,
    freeUserRatedLimited,
    projectTooBig,
    requestTooLarge,
    imageTooLarge,
    overloaded,
    billingError,
    apiError,
    networkError,
    unknownError,
    contentFilter,
    siteCreationError,
    siteDeploymentError,
    deploymentUnauthorized,
    usageBlocked,
    badServerResponse,
    loginRequiredError,
  ]);
}

export class EnhancerError extends ClientError<z.infer<typeof EnhancerError.schema>> {
  static schema = unknownError;
}

export class FixDiffError extends ClientError<z.infer<typeof fixDiffError>> {
  static schema = fixDiffError;
}

export class FixLazyError extends ClientError<z.infer<typeof FixLazyError.schema>> {
  static schema = fixLazyError;
}

export class FixBracketError extends ClientError<z.infer<typeof FixBracketError.schema>> {
  static schema = fixBracketError;
}

export class ErrorReasoningError extends ClientError<z.infer<typeof ErrorReasoningError.schema>> {
  static schema = errorReasoningError;
}

function kebabToPascal(code: string) {
  return code
    .split('-')
    .map(([first = '', ...rest]) => first.toUpperCase() + rest.join('').toLowerCase())
    .join('');
}
